/**
 * Account Prompt
 *
 * Dark dialog opened when an anonymous user presses Save or Share
 * (framing S-D4). Tier numbers come from the server, never from copy.
 */

import { useEffect, useState } from 'react';
import { fetchTiers, type TierLimits } from '../lib/api';
import type { AuthIntent } from '../context/auth-prompt';
import './Toast.css';
import './Funnel.css';

interface AccountPromptProps {
  intent: AuthIntent;
  onSignup: () => void;
  onLogin: () => void;
  onCancel: () => void;
}

export default function AccountPrompt({ intent, onSignup, onLogin, onCancel }: AccountPromptProps) {
  const [free, setFree] = useState<TierLimits | null>(null);

  useEffect(() => {
    let live = true;
    fetchTiers().then((t) => { if (live) setFree(t.free); }).catch(() => { /* copy degrades gracefully */ });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const verb = intent === 'share' ? 'share' : 'save';

  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="account-prompt-title" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-title" id="account-prompt-title">
          Create a free account to {verb} this corridor
        </div>
        <div className="confirm-message">
          Your work stays in this browser until it is saved. A free account keeps it on SigTDD and lets you share a read-only link.
          {free && (
            <ul className="account-prompt-list">
              <li>{free.maxCorridors} corridors, {free.maxPlansPerCorridor} timing plans each</li>
              <li>Up to {free.maxIntersectionsPerCorridor} intersections per corridor</li>
              <li>No credit card</li>
            </ul>
          )}
        </div>
        <div className="confirm-actions">
          <button className="confirm-btn confirm-cancel" onClick={onCancel}>Not now</button>
          <button className="confirm-btn confirm-cancel" onClick={onLogin} autoFocus>Log in</button>
          <button className="confirm-btn confirm-primary" onClick={onSignup}>Create free account</button>
        </div>
      </div>
    </div>
  );
}
